import type { Temporal } from '@js-temporal/polyfill'

const isSupportedTimeZone = (timeZone: unknown): timeZone is string => {
  if (typeof timeZone !== 'string' || !timeZone) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0)
    return true
  } catch {
    return false
  }
}

const OFFSET_TIME_ZONE = /^[+-]\d{2}:\d{2}$/
// IANA inverts the sign: Etc/GMT+7 is 7 hours behind UTC.
const ETC_GMT_TIME_ZONE = /^Etc\/GMT([+-])(\d{1,2})$/

const formatUtcOffset = (minutesAheadOfUtc: number): string => {
  const sign = minutesAheadOfUtc < 0 ? '-' : '+'
  const absoluteMinutes = Math.abs(minutesAheadOfUtc)
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, '0')
  const minutes = String(absoluteMinutes % 60).padStart(2, '0')
  return `${sign}${hours}:${minutes}`
}

const minutesAheadOfUtcIn = (timeZone: string, instant: Date): number => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    })
      .formatToParts(instant)
      .map(({ type, value }) => [type, Number(value)]),
  )
  const wallClockAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
  )
  return (wallClockAsUtc - instant.getTime()) / 60_000
}

const matchesHostOffsets = (timeZone: string, samples: Date[]): boolean =>
  samples.every(
    (sample) =>
      minutesAheadOfUtcIn(timeZone, sample) === -sample.getTimezoneOffset(),
  )

const findZoneMatchingHostOffsets = (samples: Date[]): string | undefined =>
  Intl.supportedValuesOf('timeZone').find((zone) =>
    matchesHostOffsets(zone, samples),
  )

/**
 * Returns the IANA time zone the browser reports, or `undefined` when the
 * browser cannot resolve one that `Intl` accepts. Use it for values sent to a
 * server, so an unknown zone is left out instead of guessed.
 * @returns {string | undefined} The browser's IANA time zone, if valid.
 */
export const getReportedBrowserTimeZone = (): string | undefined => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
  return isSupportedTimeZone(timeZone) ? timeZone : undefined
}

/**
 * Returns the browser's IANA time zone.
 *
 * Browsers report `undefined` or `Etc/Unknown` when the OS time zone has no
 * IANA mapping. If the browser's January and July offsets differ, this returns
 * the first IANA zone whose offsets today, in January and in July match, so
 * scheduling follows its daylight-saving changes.
 *
 * Otherwise it returns an offset time zone ID such as `+05:30` for the current
 * UTC offset. Engines that reject offset IDs get `Etc/GMT±N` for a whole-hour
 * offset, and for a fractional offset a zone matching the three samples or,
 * failing that, today's offset. `UTC` is returned only if no zone matches, or
 * at offset zero.
 *
 * It is not cached, and the fallback scans every zone: call it once, or use
 * the value Popcorn components already default to.
 * @returns {string} A time zone that `Intl` accepts.
 */
export const getBrowserTimeZone = (): string => {
  const timeZone = getReportedBrowserTimeZone()
  if (timeZone) return timeZone

  const now = new Date()
  now.setUTCSeconds(0, 0)
  const year = now.getUTCFullYear()
  const january = new Date(Date.UTC(year, 0, 1))
  const july = new Date(Date.UTC(year, 6, 1))
  const samples = [now, january, july]

  if (january.getTimezoneOffset() !== july.getTimezoneOffset()) {
    const seasonalZone = findZoneMatchingHostOffsets(samples)
    if (seasonalZone) return seasonalZone
  }

  const minutesAheadOfUtc = -now.getTimezoneOffset()
  if (minutesAheadOfUtc === 0) return 'UTC'

  const offsetZone = formatUtcOffset(minutesAheadOfUtc)
  if (isSupportedTimeZone(offsetZone)) return offsetZone
  if (minutesAheadOfUtc % 60 !== 0) {
    return (
      findZoneMatchingHostOffsets(samples) ??
      findZoneMatchingHostOffsets([now]) ??
      'UTC'
    )
  }

  const hoursAheadOfUtc = minutesAheadOfUtc / 60
  const etcGmtZone = `Etc/GMT${hoursAheadOfUtc > 0 ? '-' : '+'}${Math.abs(hoursAheadOfUtc)}`
  return isSupportedTimeZone(etcGmtZone) ? etcGmtZone : 'UTC'
}

let fallbackTimeZone: string | undefined

/**
 * The zone Popcorn components default to. A zone the browser reports is read
 * on every call, so an OS time zone change applies without a reload. The
 * fallback scans every zone, so it is computed once and reused while the
 * browser still reports none.
 * @returns {string} A time zone that `Intl` accepts.
 */
export const getDefaultTimeZone = (): string =>
  getReportedBrowserTimeZone() ?? (fallbackTimeZone ??= getBrowserTimeZone())

/**
 * Returns true for `UTC` and for fixed UTC-offset zones such as `+05:30` or
 * `Etc/GMT+7`.
 * @param {string} timeZone - The time zone to check.
 * @returns {boolean} Whether the zone has a fixed UTC offset.
 */
export const isFixedOffsetTimeZone = (timeZone: string): boolean =>
  timeZone === 'UTC' ||
  OFFSET_TIME_ZONE.test(timeZone) ||
  ETC_GMT_TIME_ZONE.test(timeZone)

/**
 * Formats a time zone for display. Underscores become spaces, and fixed-offset
 * zones read as `UTC±HH:MM`.
 * @param {string} timeZone - The time zone string to format.
 * @returns {string} The formatted time zone string.
 * @example
 * formatTimeZone('America/New_York') // "America/New York"
 * formatTimeZone('Etc/GMT+7') // "UTC-07:00"
 */
export const formatTimeZone = (timeZone: string): string => {
  if (OFFSET_TIME_ZONE.test(timeZone)) return `UTC${timeZone}`

  const etcGmt = ETC_GMT_TIME_ZONE.exec(timeZone)
  if (etcGmt) {
    const [, sign, hours] = etcGmt
    return `UTC${formatUtcOffset((sign === '+' ? -60 : 60) * Number(hours))}`
  }

  return timeZone.replace(/_/g, ' ')
}

/**
 * Formats a `PlainDate`, `PlainTime` or `PlainDateTime` for display.
 *
 * Use it instead of calling `toLocaleString` on these values. Without an
 * explicit `timeZone`, the Temporal polyfill reads the browser's zone and
 * throws when the browser cannot resolve one. These values carry no zone, so
 * formatting them in UTC shows the same wall-clock value.
 * @param {Temporal.PlainDate | Temporal.PlainTime | Temporal.PlainDateTime} value - The value to format.
 * @param {string | string[]} locales - The locale, or `undefined` for the browser's.
 * @param {Intl.DateTimeFormatOptions} options - Formatting options, without `timeZone`.
 * @returns {string} The formatted value.
 */
export const formatPlainTemporal = (
  value: Temporal.PlainDate | Temporal.PlainTime | Temporal.PlainDateTime,
  locales?: string | string[],
  options?: Omit<Intl.DateTimeFormatOptions, 'timeZone'>,
): string => value.toLocaleString(locales, { ...options, timeZone: 'UTC' })
